/**
 * DepartmentTabs Component
 *
 * Displays clickable tiles for each department.
 * Allows users to filter the forecast view by department.
 */

import React, { useMemo } from 'react';
import {
  TruckIcon,
  WrenchScrewdriverIcon,
  CubeIcon,
  SparklesIcon,
  KeyIcon,
  EllipsisHorizontalCircleIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';

export type DepartmentKey =
  | 'ALL'
  | 'NEW_VEHICLE'
  | 'USED_VEHICLE'
  | 'SERVICE'
  | 'PARTS'
  | 'BODY_SHOP'
  | 'RENTAL'
  | 'OTHERS';

interface Department {
  key: DepartmentKey;
  label: string;
  shortLabel: string;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  bgColor: string;
  borderColor: string;
}

const DEPARTMENTS: Department[] = [
  {
    key: 'ALL',
    label: 'All Departments',
    shortLabel: 'All',
    icon: Squares2X2Icon,
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
    borderColor: 'border-gray-300',
  },
  {
    key: 'NEW_VEHICLE',
    label: 'New Vehicles',
    shortLabel: 'New',
    icon: TruckIcon,
    color: 'text-blue-700',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-300',
  },
  {
    key: 'USED_VEHICLE',
    label: 'Used Vehicles',
    shortLabel: 'Used',
    icon: TruckIcon,
    color: 'text-green-700',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-300',
  },
  {
    key: 'SERVICE',
    label: 'Service',
    shortLabel: 'Service',
    icon: WrenchScrewdriverIcon,
    color: 'text-orange-700',
    bgColor: 'bg-orange-50',
    borderColor: 'border-orange-300',
  },
  {
    key: 'PARTS',
    label: 'Parts',
    shortLabel: 'Parts',
    icon: CubeIcon,
    color: 'text-purple-700',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-300',
  },
  {
    key: 'BODY_SHOP',
    label: 'Body Shop',
    shortLabel: 'Body',
    icon: SparklesIcon,
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-300',
  },
  {
    key: 'RENTAL',
    label: 'Rental',
    shortLabel: 'Rental',
    icon: KeyIcon,
    color: 'text-teal-700',
    bgColor: 'bg-teal-50',
    borderColor: 'border-teal-300',
  },
  {
    key: 'OTHERS',
    label: 'Others',
    shortLabel: 'Others',
    icon: EllipsisHorizontalCircleIcon,
    color: 'text-slate-700',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-300',
  },
];

/** Location-specific department visibility (ALL is always included) */
const LOCATION_DEPARTMENTS: Record<number, DepartmentKey[]> = {
  12: ['BODY_SHOP', 'RENTAL'],     // Green Point: Body Shop + Rental only
  44: ['BODY_SHOP'],               // Undercoating & More: Body Shop only
};

interface DepartmentTabsProps {
  selectedDepartment: DepartmentKey;
  onDepartmentChange: (department: DepartmentKey) => void;
  locationId?: number;
  assignedDepartmentIds?: string[];
}

export const DepartmentTabs: React.FC<DepartmentTabsProps> = ({
  selectedDepartment,
  onDepartmentChange,
  locationId,
  assignedDepartmentIds,
}) => {
  const visibleDepartments = useMemo(() => {
    // Start with all departments
    let filtered = DEPARTMENTS;

    // Apply location-specific filtering (e.g., location 12 = Body Shop + Rental only)
    const locationAllowed = locationId ? LOCATION_DEPARTMENTS[locationId] : undefined;
    if (locationAllowed) {
      filtered = filtered.filter(d => d.key === 'ALL' || locationAllowed.includes(d.key));
    }

    // Apply department assignment filtering for DepartmentManagers
    if (assignedDepartmentIds && assignedDepartmentIds.length > 0) {
      filtered = filtered.filter(d => d.key !== 'ALL' && assignedDepartmentIds.includes(d.key));
    }

    return filtered;
  }, [locationId, assignedDepartmentIds]);

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
      <h3 className="text-sm font-medium text-gray-700 mb-3">Select Department</h3>
      <div className="grid grid-cols-4 md:grid-cols-8 gap-2">
        {visibleDepartments.map((dept) => {
          const isSelected = selectedDepartment === dept.key;
          const Icon = dept.icon;

          return (
            <button
              key={dept.key}
              onClick={() => onDepartmentChange(dept.key)}
              className={`
                flex flex-col items-center justify-center p-3 rounded-lg border-2 transition-all
                ${isSelected
                  ? `${dept.bgColor} ${dept.borderColor} ${dept.color} shadow-sm`
                  : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:border-gray-300'
                }
              `}
            >
              <Icon className={`h-6 w-6 mb-1 ${isSelected ? dept.color : 'text-gray-400'}`} />
              <span className={`text-xs font-medium ${isSelected ? dept.color : 'text-gray-600'}`}>
                {dept.shortLabel}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Export department info for use in other components
export const getDepartmentInfo = (key: DepartmentKey): Department | undefined => {
  return DEPARTMENTS.find(d => d.key === key);
};

export const getDepartmentLabel = (key: DepartmentKey): string => {
  return DEPARTMENTS.find(d => d.key === key)?.label || key;
};
